import { DataStore, Query, QueryResult } from 'src/core/stores/dataStore'
import { FormStore } from 'src/core/stores/formStore'
import { repository } from 'redux-scaffolding-ts'
import { container } from 'src/inversify.config'
import { Validator } from "lakmus"
import HttpService from '../core/services/http.service'
import autobind from 'autobind-decorator'
import { AxiosResponse } from "axios";

type TimesheetStatus = 'Draft' | 'Pending' | 'Approved' | 'Rejected'

export interface TimesheetSummary {
  id: string
  fromDate: number
  toDate: number
  employeeId: string
  employeeTitle: string
  status: TimesheetStatus
  totalHours: number
  minHours: number
  percentage: number
  employeeRestrictionPlan: string
  inFuture : boolean
}

export interface TimesheetsGeneratorItem {
  fromDate: number
  toDate: number
  employees: string[]
}

export interface TimesheetItem extends TimesheetSummary {

}

export interface NewTimesheetItem {
  fromDate: number
  toDate: number
  employeeId: string
}

export interface TimesheetProjectMode {
  projectId: string
  projectTitle: string
  projectTask: any[]
}

@repository("@@TIMESHEETS", "timesheets.summary")
export class TimesheetsStore extends DataStore<TimesheetSummary> {
  baseUrl: string = `${process.env.PUBLIC_URL}/api/v1/admin`;

  constructor() {
    super('TIMESHEET', {
      count: 0,
      isBusy: false,
      items: [],
      result: undefined,
      discard: item => { }
    }, container)
  }

  public setBaseUrl(companyId?: string) {
    this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/admin/companies/${companyId}/timesheets`
  }

  @autobind
  public setBaseUrlForEmployees(companyId?: string) {
    this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/admin/companies/${companyId}/timesheets/employees`
  }

  @autobind
  public setBaseUrlForProject(companyId?: string, projectId?: string) {
    this.baseUrl = `${process.env.PUBLIC_URL}/api/v1/admin/companies/${companyId}/timesheets/${projectId}/summary`
  }

  public async getAllCustomAsync(requestUrl: string, query: Query, data?: any): Promise<any> {
    let httpService = this.container.get<HttpService>(HttpService)
    const result = await this.dispatchAsync(this.ENTITY_LIST_UPDATE as string, httpService.get<any>(`${requestUrl}?${DataStore.buildUrl(query)}`, data))
    return result
  }

  public async getProjectEmployeeAsync(employeeId: string): Promise<any[]> {
    let httpService = this.container.get<HttpService>(HttpService)
    const result = await httpService.get<any[]>(`${this.baseUrl}/projectsAlls/${employeeId}`, {})
    return result.data
  }

  public async generateTimesheetsWeek(employeeId: string, cloneTimesheetManual: string, projectIds: TimesheetProjectMode[], timesheetId: string = '00000000-0000-0000-0000-000000000000') {
    let httpService = this.container.get<HttpService>(HttpService)
    const result = await httpService.post(`${this.baseUrl}/${employeeId}/${cloneTimesheetManual}/generateWeek/${timesheetId}`, projectIds)
    return result.data
  }
  public async checkEmployeeExists(companyId: string): Promise<boolean | null> {
    let httpService = this.container.get<HttpService>(HttpService);
    this.setBaseUrl(companyId)
    const result: AxiosResponse<boolean | null>  = await httpService.get(`${this.baseUrl}/checkEmployeeExists`, {});
    return result.data;
  }
  public async getCompanyTimesheetManual(hoursManager: boolean = false): Promise<boolean> {
    let httpService = this.container.get<HttpService>(HttpService)
    const result = await httpService.get(`${this.baseUrl}/companyCloneTimesheetManual/${hoursManager}`, {})
    if (typeof result.data === 'boolean') {
      return result.data
    } else {
      throw new Error('Response data is not of type boolean')
    }
  }
}

export class NewTimesheetValidator extends Validator<NewTimesheetItem> {
  constructor() {
    super()

    this.ruleFor(x => x.employeeId)
      .notNull()
      .withMessage("Employee id is required")

    this.ruleFor(x => x.fromDate)
      .notNull()
      .withMessage("From date is required")

    this.ruleFor(x => x.toDate)
      .notNull()
      .withMessage("To date is required")
  }
}

@repository("@@TIMESHEETS", "timesheets.detail")
export class TimesheetStore extends FormStore<TimesheetItem> {
  baseUrl: string = `${process.env.PUBLIC_URL}/api/v1/admin/{companyId}/timesheets`;

  protected validate(item: TimesheetItem) {
    return undefined
  }

  constructor() {
    super('TIMESHEET', {
      isBusy: false,
      status: 'New',
      item: undefined,
      result: undefined
    }, container)
  }
}

@repository("@@TIMESHEETS", "timesheets.new")
export class NewTimesheetStore extends FormStore<NewTimesheetItem> {
  baseUrl: string = `${process.env.PUBLIC_URL}/api/v1/admin/{companyId}/timesheets`;

  protected validate(item: NewTimesheetItem) {
    return (new NewTimesheetValidator()).validate(item)
  }

  constructor() {
    super('NEW_TIMESHEET', {
      isBusy: false,
      status: 'New',
      item: undefined,
      result: undefined
    }, container)
  }
}